import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve, RouterStateSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class PageConfigDataResolver implements Resolve<any> {
  // Use static cache to ensure all instances share the same cache
  // This prevents duplicate requests when multiple resolver instances are created
  // Cache Observable<any> to share in-flight requests, not just completed data
  private static cache = new Map<string, Observable<any>>();
  private instanceId = Math.random().toString(36).substring(7);

  constructor(private http: HttpClient) {}

  /**
   * Normalizes the path to ensure consistent caching
   * Splits by "/" and returns the JSON filename (last segment with ".json")
   * 
   * Examples:
   * - '../assets/page-configurations/downsells/DS_1_Unit_NaturesBlend_AP.json' -> 'DS_1_Unit_NaturesBlend_AP.json'
   * - '../../assets/page-configurations/downsells/DS_1_Unit_NaturesBlend_AP.json' -> 'DS_1_Unit_NaturesBlend_AP.json'
   */
  private normalizePath(path: string): string {
    if (!path) return path;
    
    // Remove any query strings or hashes
    const cleanPath = path.split('?')[0].split('#')[0].trim();
    
    // Split by "/"
    const segments = cleanPath.split('/');
    
    // Find the last index that includes ".json"
    for (let i = segments.length - 1; i >= 0; i--) {
      if (segments[i].includes('.json')) {
        return segments[i];
      }
    }
    
    // If no JSON file found, return the original path
    return cleanPath;
  }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {
    const pageConfigPath = route.data['pageConfigPath'] as string;
    const routePath = route.routeConfig?.path || 'unknown';

    if (!pageConfigPath) {
      console.warn('PageConfigDataResolver: No pageConfigPath provided in route data for route:', routePath);
      return of(null);
    }

    const normalizedPath = this.normalizePath(pageConfigPath);

    // Check if we have a cached observable for this path (handles both in-flight and completed requests)
    // Use static cache to ensure all instances share the same cache
    if (PageConfigDataResolver.cache.has(normalizedPath)) {
      const cached$ = PageConfigDataResolver.cache.get(normalizedPath)!;
      return cached$.pipe(
        map((cachedData: any) => {
          // Merge the static data with the loaded JSON data
          route.data = { ...cachedData, ...route.data };
          return { ...cachedData, ...route.data };
        }),
        catchError(error => {
          // Handle errors for cached observables - errors are multicast via shareReplay(1)
          // so all concurrent subscribers need error handling, not just the first one
          console.error('PageConfigDataResolver: Loading configuration failed for cached', normalizedPath, 'route:', routePath, error, 'instance:', this.instanceId);
          // Remove from cache on error so it can be retried
          PageConfigDataResolver.cache.delete(normalizedPath);
          return of(null);
        })
      );
    }

    // Create a new request with shareReplay to handle concurrent requests
    // shareReplay(1) ensures that multiple subscribers share the same HTTP request
    const request$ = this.http.get(pageConfigPath).pipe(
      shareReplay(1) // Share the result and replay it for subsequent subscribers - CRITICAL for preventing duplicate requests
    );

    // Cache the observable SYNCHRONOUSLY before returning to prevent race conditions
    // This is safe because:
    // 1. The Observable is created synchronously (no HTTP request has started yet)
    // 2. shareReplay(1) is applied synchronously
    // 3. Caching it immediately allows concurrent calls to find it and share the same request
    // 4. The Observable itself is what we cache - it will emit data when ready
    PageConfigDataResolver.cache.set(normalizedPath, request$);

    // Return the observable with error handling and data transformation
    return request$.pipe(
      map(data => {
        // Merge the static data with the loaded JSON data
        route.data = { ...data, ...route.data };
        return { ...data, ...route.data };
      }),
      catchError(error => {
        console.error('PageConfigDataResolver: Loading configuration failed for', normalizedPath, 'route:', routePath, error, 'instance:', this.instanceId);
        // Remove from cache on error so it can be retried
        PageConfigDataResolver.cache.delete(normalizedPath);
        return of(null);
      })
    );
  }
}